import * as ApolloCommon from '@apollo/client';
import { Check, Clear } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  InputBase,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { FormControlLabelProps } from '@mui/material/FormControlLabel';
import { SelectProps } from '@mui/material/Select';
import * as Schema from 'generated/graphql/schema';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { compose } from 'recompose';

import { MutationIconButton } from '@/components/buttons/mutation-button';
import { WithStyles, withStyles } from '@/hocs/with-styles';

import { StopCauseMapping } from './stops-mapping-tab';

const styles = () => ({
  widest: {
    width: '50%',
  },
  secondWidest: {
    width: '20%',
  },
  selectPlaceholder: {
    color: 'inherit',
    opacity: 0.46,
  },
});

interface Properties {
  peripheralId: string;
  deviceOwner?: string;
  stopCauses: Array<{ name: string; id: string }>;
  toMappingInput: (mapping: StopCauseMapping) => Schema.StopCauseMappingInput;
  updateStopCauseMapping: ApolloCommon.MutationFunction<Schema.DeviceUpdateStopCauseMappingMutation>;
  permission: boolean;
  loading: boolean;
  setError: (error: boolean) => void;
  mapping?: StopCauseMapping;
  stopEditing?: () => void;
}

type ExtendedProps = Properties & WithStyles<typeof styles> & WithTranslation;

interface State {
  mapping: StopCauseMapping;
  modifyingThis: boolean;
}

const MAX_BUFFER_VALUE = (1).hours;

class NewStopMapping extends React.Component<ExtendedProps, State> {
  readonly state: State;

  readonly baseMap: StopCauseMapping;

  constructor(props: ExtendedProps) {
    super(props);

    this.baseMap = {
      stopCauseName: undefined,
      stopCauseId: '',
      mappingTag: '',
      mappingValue: '',
      regPriority: 1,
      split: false,
      lookBack: 0,
      lookForward: 0,
    };

    this.state = {
      mapping: props.mapping ? props.mapping : this.baseMap,
      modifyingThis: false,
    };
  }

  public shouldComponentUpdate(nextProps: ExtendedProps) {
    if (nextProps.loading === false && this.props.loading === true) {
      this.setState({ mapping: this.baseMap });
    }
    return true;
  }

  public render() {
    const { classes, permission, loading, stopCauses, stopEditing, t, mapping: originialMapping } = this.props;
    const { mapping: updatedMapping } = this.state;

    const lookForwardInSeconds = Math.floor(updatedMapping.lookForward / 1000);
    const lookBackInSeconds = Math.floor(updatedMapping.lookBack / 1000);

    return (
      <TableRow key="new-stop-mapping">
        <TableCell component="th" scope="row" className={classes.widest}>
          <InputBase
            value={updatedMapping.mappingTag}
            key="new-mapping-tag"
            fullWidth
            onChange={this.handleTagChange}
            placeholder={t(['shared:new'], { defaultValue: 'New' })}
          />
        </TableCell>
        <TableCell>
          <InputBase
            value={updatedMapping.mappingValue}
            key="new-mapping-value"
            onChange={this.handleValueChange}
            fullWidth
            placeholder={t(['shared:new'], { defaultValue: 'New' })}
          />
        </TableCell>
        <TableCell className={classes.secondWidest}>
          <Select
            value={updatedMapping.stopCauseName}
            variant="filled"
            key="new-mapping-stop-cause"
            onChange={this.handleStopCauseChange}
            fullWidth
            disableUnderline
            displayEmpty
          >
            <MenuItem value={undefined} disabled>
              <Typography className={classes.selectPlaceholder}>
                {t(['shared:none'], { defaultValue: 'None' })}
              </Typography>
            </MenuItem>
            {stopCauses.map((stopCause) => (
              <MenuItem value={stopCause.name} key={`${stopCause.id}`}>
                {stopCause.name}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>
          <Select
            value={updatedMapping.regPriority}
            variant="filled"
            key="new-mapping-priority"
            onChange={this.handlePriorityChange}
            fullWidth
            input={<InputBase style={{ minWidth: 35 }} />}
          >
            <FormControlLabel
              style={{ marginLeft: 0 }}
              control={<Checkbox checked={updatedMapping.split} />}
              onChange={this.handleSplit}
              label={t(['stop:splitStop'], { defaultValue: 'Split stop' })}
            />
            {[1, 2, 3, 4, 5].map((rank) => (
              <MenuItem value={rank} key={`${updatedMapping.stopCauseName}_${rank}`}>
                {rank}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>
          <InputBase
            value={lookBackInSeconds}
            key="new-mapping-lookback"
            onChange={this.handleLookBackChange}
            fullWidth
          />
        </TableCell>
        <TableCell>
          <InputBase
            value={lookForwardInSeconds}
            key="new-mapping-lookforward"
            onChange={this.handleLookForwardChange}
            fullWidth
          />
        </TableCell>
        <TableCell>
          {this.props.mapping ? (
            <MutationIconButton
              color="primary"
              loading={loading && this.state.modifyingThis}
              onClick={this.updateStopMapping}
              disabled={permission}
              tooltipPlacement="left"
              tooltipMsg={t(['shared:update'], { defaultValue: 'Update' })}
              tooltipMsgDisabled={t(['device:noPermissionToChangeConfiguration'], {
                defaultValue: "You don't have permission to change the sensor configuration",
              })}
            >
              <Check />
            </MutationIconButton>
          ) : (
            <MutationIconButton
              color="primary"
              loading={loading && this.state.modifyingThis}
              onClick={this.createStopMapping}
              disabled={permission}
              tooltipPlacement="right"
              tooltipMsg={t(['device:createNewStopMapping'], { defaultValue: 'Create new stop cause mapping' })}
              tooltipMsgDisabled={t(['device:noPermissionToChangeConfiguration'], {
                defaultValue: "You don't have permission to change the sensor configuration",
              })}
            >
              <Check />
            </MutationIconButton>
          )}
        </TableCell>
        <TableCell>
          {this.props.mapping && (
            <MutationIconButton
              color="primary"
              onClick={stopEditing}
              disabled={permission}
              tooltipPlacement="right"
              tooltipMsg={t(['shared:discard'], { defaultValue: 'Discard' })}
              tooltipMsgDisabled={t(['device:noPermissionToChangeConfiguration'], {
                defaultValue: "You don't have permission to change the sensor configuration",
              })}
            >
              <Clear />
            </MutationIconButton>
          )}
        </TableCell>
      </TableRow>
    );
  }

  private updateStopMapping = async () => {
    const { deviceOwner, peripheralId, toMappingInput, updateStopCauseMapping, stopEditing } = this.props;
    this.setState({ modifyingThis: true });
    if (this.props.mapping && this.checkForm()) {
      const oldStopMap = toMappingInput(this.props.mapping);
      const newStopMap = toMappingInput(this.state.mapping);

      await updateStopCauseMapping({
        variables: {
          deviceOwner,
          peripheralId,
          deleteStopCauseMapping: [oldStopMap],
          createStopCauseMapping: [newStopMap],
        },
      });

      stopEditing?.();
    }
    this.setState({ modifyingThis: false });
  };

  private createStopMapping = async () => {
    const { deviceOwner, peripheralId, toMappingInput, updateStopCauseMapping } = this.props;
    this.setState({ modifyingThis: true });
    if (this.checkForm()) {
      const stopMap = toMappingInput(this.state.mapping);
      await updateStopCauseMapping({
        variables: {
          deviceOwner,
          peripheralId,
          deleteStopCauseMapping: [],
          createStopCauseMapping: [stopMap],
        },
      });
    }
    this.setState({ modifyingThis: false });
  };

  private checkForm = () => {
    const { mapping } = this.state;

    const stopCauseName = mapping.stopCauseName === '' || mapping.stopCauseName === undefined;
    const stopCauseId = mapping.stopCauseId === '' || mapping.stopCauseId === undefined;
    const mappingTag = mapping.mappingTag === '' || mapping.mappingTag === undefined;
    const mappingValue = mapping.mappingValue === '' || mapping.mappingValue === undefined;
    const validPriority = 1 <= mapping.regPriority && mapping.regPriority <= 5;
    const validLookBack = 0 <= mapping.lookBack && mapping.lookBack < MAX_BUFFER_VALUE;
    const validLookForward = 0 <= mapping.lookForward && mapping.lookForward < MAX_BUFFER_VALUE;

    const error =
      stopCauseName ||
      stopCauseId ||
      mappingTag ||
      mappingValue ||
      !validPriority ||
      !validLookBack ||
      !validLookForward;

    this.props.setError(error);
    return !error;
  };

  private handleTagChange = ({ target: { value: mappingTag } }: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ mapping: { ...this.state.mapping, mappingTag } });

  private handleValueChange = ({ target: { value: mappingValue } }: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ mapping: { ...this.state.mapping, mappingValue } });

  private handleStopCauseChange: SelectProps['onChange'] = ({ target: { value: name } }) => {
    if (typeof name !== 'string') {
      return;
    }

    const id = this.props.stopCauses.find((stopCause) => stopCause.name === name)?.id || '';
    this.setState({ mapping: { ...this.state.mapping, stopCauseId: id, stopCauseName: name } });
  };

  private handlePriorityChange: SelectProps['onChange'] = ({ target: { value } }) => {
    if (typeof value !== 'number') {
      return;
    }

    this.setState({ mapping: { ...this.state.mapping, regPriority: value } });
  };

  private handleSplit: FormControlLabelProps['onChange'] = (_, checked) => {
    this.setState({ mapping: { ...this.state.mapping, split: checked } });
  };

  private handleLookChange =
    (attribute: 'lookBack' | 'lookForward') =>
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      const valueAsNumber = Number(value);

      if (isNaN(valueAsNumber)) {
        return;
      }

      const valueInMilliseconds = valueAsNumber * 1000;

      if (MAX_BUFFER_VALUE < valueInMilliseconds) {
        return;
      }

      this.setState({
        mapping: {
          ...this.state.mapping,
          [attribute]: valueInMilliseconds,
        },
      });
    };

  private handleLookBackChange = this.handleLookChange('lookBack');
  private handleLookForwardChange = this.handleLookChange('lookForward');
}

const enhance = compose<unknown, Properties>(withStyles(styles), withTranslation(['shared', 'device']));

export default enhance(NewStopMapping as React.ComponentType<unknown>);
